import React, { useEffect, useState, useRef, useEffect as useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "./css/AnalysisView.css";
import API_BASE from "./config"; 
import { usePermissions, RequirePermission } from "./hooks/usePermissions";

const AnalysisView = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const workflowId = location?.state?.workflowId;
    const isByop = !!location?.state?.isByop;

    const [executionHistory, setExecutionHistory] = useState([]);
    const [selectedLog, setSelectedLog] = useState("");

    const [expandedResults, setExpandedResults] = useState({});
    const [expandedErrors, setExpandedErrors] = useState({}); // jobId -> bool (error panel visible)
    const [expandedPipeline, setExpandedPipeline] = useState({}); // jobId -> bool (pipeline progress panel)
    const [pipelineData, setPipelineData] = useState({}); // jobId -> progress payload
    const pipelinePolls = useRef({}); // jobId -> setInterval handle (live refresh while running)
    const [resultsTree, setResultsTree] = useState({});
    const [partialResults, setPartialResults] = useState({}); // jobId -> [{name,step,size,url}] for failed/incomplete runs
    const [selectedFiles, setSelectedFiles] = useState({});
    const [expandedReports, setExpandedReports] = useState({});
    const [reportHtmlBlobs, setReportHtmlBlobs] = useState({}); // jobId -> blob URL
    const [htmlViewModal, setHtmlViewModal] = useState(null); // { url, title } — Results .html viewer
    const [expandedFolders, setExpandedFolders] = useState({});
    const [interpretModalJobId, setInterpretModalJobId] = useState(null);
    const [isInterpreting, setIsInterpreting] = useState({}); // jobId -> boolean (submission in progress)
    // Save-results modal: { jobId, defaultName } while the Project Name prompt is open.
    const [saveModal, setSaveModal] = useState(null);
    const [saveProject, setSaveProject] = useState("");
    const [savingResults, setSavingResults] = useState(false);
    const [downloadingJobs, setDownloadingJobs] = useState({}); // jobId -> true while the zip is being compiled
    const [saveNotice, setSaveNotice] = useState(null); // banner after a save is launched
    const [showSaveTasks, setShowSaveTasks] = useState(false);
    const [saveTasks, setSaveTasks] = useState([]);
    const [activeTab, setActiveTab] = useState(null);
    const [activeExecutionId, setActiveExecutionId] = useState(null);

    // Analysis storage (per s3_folder_path = one run + its relaunches): total size +
    // remove. folderSizes maps path -> bytes (null while loading, -1 on error).
    const [folderSizes, setFolderSizes] = useState({});
    const sizeRequested = useRef(new Set());
    const [removeTarget, setRemoveTarget] = useState(null); // { path, label } being confirmed
    const [cleaningPaths, setCleaningPaths] = useState({}); // s3FolderPath -> true while run files are being removed
    const [removing, setRemoving] = useState(false);
    const [expandedRunDetails, setExpandedRunDetails] = useState({}); // jobId -> bool (inline Run Details)

    // Interpretations per execution
    const [interpretationList, setInterpretationList] = useState({}); // executionId -> [rows]
    const [expandedInterpretations, setExpandedInterpretations] = useState({}); // executionId -> bool
    const [interpretationModalHtml, setInterpretationModalHtml] = useState(null);
    const [interpretationModalTitle, setInterpretationModalTitle] = useState("");
    const [interpretationUserNotes, setInterpretationUserNotes] = useState(""); // new
    const [userCredits, setUserCredits] = useState(null);
    const insufficientCredits = userCredits !== null && userCredits < 50;

    // Interpretation config (allowed file types) — fetched from the backend so the
    // gate always matches the DB-managed CLAUDE_INTERPRETATION_ALLOWED_FILE_TYPES.
    // Seeded with the built-in default so the UI works before the fetch resolves.
    const [interpretConfig, setInterpretConfig] = useState({
        allowedFileTypes: [".bed", ".txt", ".html", ".vcf", ".json", ".tsv", ".csv"],
        maxFileSize: 5000000
    });
    // When a user tries to interpret disallowed files, we show a styled popup
    // (never window.alert). Holds { files: [...] } while open.
    const [disallowedModal, setDisallowedModal] = useState(null);

    useEffect(() => {
        const fetchInterpretConfig = async () => {
            try {
                const res = await axios.get(`${API_BASE}/interpretation-config`, {
                    withCredentials: true
                });
                if (res.data && Array.isArray(res.data.allowedFileTypes) && res.data.allowedFileTypes.length) {
                    setInterpretConfig({
                        allowedFileTypes: res.data.allowedFileTypes.map(t => String(t).toLowerCase()),
                        maxFileSize: res.data.maxFileSize || 5000000
                    });
                }
            } catch (err) {
                console.error("Error fetching interpretation config:", err);
            }
        };
        fetchInterpretConfig();
    }, []);

    // True if a file path/name has an allowed extension for interpretation.
    const isAllowedForInterpretation = (nameOrPath) => {
        const lower = String(nameOrPath || "").toLowerCase();
        return interpretConfig.allowedFileTypes.some(ext => lower.endsWith(ext));
    };

    useEffect(() => {
        const fetchCredits = async () => {
            try {
                const res = await fetch(`${API_BASE}/user/credits`, {
                    credentials: "include"
                });
                const data = await res.json();
                setUserCredits(data.balance);
            } catch (err) {
                console.error("Error fetching user credits:", err);
                setUserCredits(0);
            }
        };

        fetchCredits();
    }, []);



    const hasSelectedFiles = (jobId) => {
        const selections = selectedFiles[jobId] || {};
        return Object.values(selections).some(checked => checked);
    };

    // Cleanup blob URLs on unmount
    useEffect(() => {
        return () => {
            Object.values(reportHtmlBlobs).forEach(url => {
                try {
                    URL.revokeObjectURL(url);
                } catch (e) {}
            });
        };
    }, [reportHtmlBlobs]);

    useEffect(() => {
        const fetchExecutionHistory = async () => {
            try {
                const response = await axios.get(
                    `${API_BASE}/getExecutionHistory?workflowId=${workflowId}${isByop ? "&is_byop=1" : ""}`
                );
                setExecutionHistory(response.data.history);
            } catch (error) {
                console.error("Error fetching execution history:", error);
            }
        };
        if (workflowId) {
            fetchExecutionHistory();
        }
    }, [workflowId, isByop]);

    // Fetch the total S3 size of each distinct analysis folder once it appears in the
    // history. Keyed on s3_folder_path so relaunches (same folder) are counted once.
    useEffect(() => {
        const paths = [...new Set(executionHistory.map((e) => e.s3_folder_path).filter(Boolean))];
        paths.forEach((p) => {
            if (sizeRequested.current.has(p)) return;
            sizeRequested.current.add(p);
            setFolderSizes((prev) => ({ ...prev, [p]: null }));
            axios
                .get(`${API_BASE}/analysis/folder-size`, { params: { s3FolderPath: p }, withCredentials: true })
                .then((res) => setFolderSizes((cur) => ({ ...cur, [p]: res.data.bytes })))
                .catch(() => setFolderSizes((cur) => ({ ...cur, [p]: -1 })));
        });
    }, [executionHistory]);

    // Confirmed removal: delete the S3 folder (+ Output mirror) and all execution rows
    // for this run, then drop it from the UI.
    const confirmRemoveAnalysis = async () => {
        if (!removeTarget) return;
        setRemoving(true);
        try {
            await axios.post(
                `${API_BASE}/analysis/remove`,
                { s3FolderPath: removeTarget.path },
                { withCredentials: true }
            );
            setExecutionHistory((prev) => prev.filter((e) => e.s3_folder_path !== removeTarget.path));
            setFolderSizes((prev) => {
                const n = { ...prev };
                delete n[removeTarget.path];
                return n;
            });
            sizeRequested.current.delete(removeTarget.path);
            setRemoveTarget(null);
        } catch (err) {
            console.error("Remove analysis failed:", err);
            alert(err.response?.data?.error || "Failed to remove analysis.");
        } finally {
            setRemoving(false);
        }
    };

    // A run is "active" (not yet terminal) if any of its attempts is still in flight.
    const ACTIVE_STATUSES = ["running", "processing", "launched", "queued", "submitted"];
    // A run FOLDER is active if any of its executions (relaunches share a folder) is in flight.
    const folderActive = (path) => executionHistory.some((e) => e.s3_folder_path === path && ACTIVE_STATUSES.includes(String(e.status || "").toLowerCase()));

    // Reclaim space: delete only the intermediate .nextflow/ + work/ dirs from the run
    // folder (keeps results/reports/logs). The run can no longer be resumed afterwards.
    const removeRunFiles = async (path) => {
        const label = String(path || "").split("/").filter(Boolean).pop();
        if (!path || cleaningPaths[path]) return;
        if (!window.confirm(`Remove intermediate run files (.nextflow + work) for "${label}"?\n\nThis frees storage but the run can no longer be resumed. Your results, reports and logs are kept.`)) return;
        setCleaningPaths((p) => ({ ...p, [path]: true }));
        try {
            await axios.post(`${API_BASE}/analysis/remove-run-files`, { s3FolderPath: path }, { withCredentials: true });
            // Refresh the displayed folder size.
            sizeRequested.current.delete(path);
            setFolderSizes((prev) => ({ ...prev, [path]: null }));
            try {
                const res = await axios.get(`${API_BASE}/analysis/folder-size`, { params: { s3FolderPath: path }, withCredentials: true });
                setFolderSizes((cur) => ({ ...cur, [path]: res.data.bytes }));
            } catch (_) { setFolderSizes((cur) => ({ ...cur, [path]: -1 })); }
            sizeRequested.current.add(path);
        } catch (err) {
            console.error("Remove run files failed:", err);
            alert(err.response?.data?.error || "Failed to remove run files.");
        } finally {
            setCleaningPaths((p) => ({ ...p, [path]: false }));
        }
    };

    const fetchLogsForExecution = async (jobId) => {
        try {
            const response = await axios.get(`${API_BASE}/fetchCloudWatchLogs?jobId=${jobId}`);
            setSelectedLog(response.data.logs || "No logs available.");
        } catch (error) {
            console.error(`Error fetching logs for Job ID ${jobId}:`, error);
            setSelectedLog("Error fetching logs.");
        }
    };

    // Build tree: display uses relative path (file.path), selection uses full_s3_key (file.full_s3_key)
    const buildTree = (files) => {
        const root = { name: "root", type: "folder", children: {} };
        files.forEach(file => {
            if (!file.path) return;
            const relativePath = file.path.replace(/^.*?output\//, ""); // grouping only
            const parts = relativePath.split("/").filter(Boolean);
            let current = root;
            parts.forEach((part, idx) => {
                if (!current.children[part]) {
                    current.children[part] = {
                        name: part,
                        type: idx === parts.length - 1 ? "file" : "folder",
                        children: {},
                        url: file.url,
                        // full_s3_key for backend operations
                        path: file.full_s3_key || file.path,
                        displayPath: file.path,
                        // bytes — only the file leaf carries a real size
                        size: idx === parts.length - 1 ? file.size : undefined
                    };
                }
                current = current.children[part];
            });
        });
        return root.children;
    };

    const toggleErrorPanel = (jobId) => {
        const jobIdStr = String(jobId);
        setExpandedErrors((prev) => ({ ...prev, [jobIdStr]: !prev[jobIdStr] }));
    };

    // ---- Pipeline progress (declared processes from main.nf + trace status) ----
    const statusColor = (s) => ({
        completed: "#16a34a", cached: "#64748b", running: "#2563eb", failed: "#dc2626", pending: "#cbd5e1",
    }[s] || "#94a3b8");

    const fetchPipeline = async (jobId) => {
        try {
            const res = await axios.get(`${API_BASE}/execution-progress?jobId=${encodeURIComponent(jobId)}`);
            setPipelineData((prev) => ({ ...prev, [String(jobId)]: res.data }));
        } catch (e) {
            console.error("pipeline fetch error:", e);
            setPipelineData((prev) => ({ ...prev, [String(jobId)]: { error: "Could not load pipeline progress." } }));
        }
    };

    const togglePipeline = async (entry) => {
        const jobIdStr = String(entry.job_id);
        if (expandedPipeline[jobIdStr]) {
            setExpandedPipeline((prev) => ({ ...prev, [jobIdStr]: false }));
            if (pipelinePolls.current[jobIdStr]) { clearInterval(pipelinePolls.current[jobIdStr]); delete pipelinePolls.current[jobIdStr]; }
            return;
        }
        await fetchPipeline(entry.job_id);
        setExpandedPipeline((prev) => ({ ...prev, [jobIdStr]: true }));
        // Live-refresh every 30s (matching the trace's S3 sync) while the run is active.
        const running = ["running", "processing", "launched", "queued", "submitted"].includes(String(entry.status).toLowerCase());
        if (running && !pipelinePolls.current[jobIdStr]) {
            pipelinePolls.current[jobIdStr] = setInterval(() => fetchPipeline(entry.job_id), 30000);
        }
    };

    // Clear any live-refresh intervals on unmount.
    useEffect(() => () => { Object.values(pipelinePolls.current).forEach(clearInterval); }, []);

    const toggleResultsPanel = async (entry) => {
        const jobIdStr = String(entry.job_id);
        const isExpanded = expandedResults[jobIdStr];
        if (isExpanded) {
            setExpandedResults(prev => ({ ...prev, [jobIdStr]: false }));
            return;
        }
        try {
            let files = [];
            let partial = [];
            if (entry.workflow_type === "generated" || entry.workflow_type === "byop" || entry.workflow_type === "designer") {
                // Generated, BYOP and Designer runs all publish to <s3_folder>/output/
                // and use the same /get-results endpoint. get-results returns 404 for a
                // run with no finished 'output/'; axios throws on 404, so isolate it and
                // fall through to partial recovery.
                try {
                    const res = await axios.get(`${API_BASE}/get-results`, { params: { jobId: entry.job_id } });
                    files = res.data.files || [];
                } catch (_) { files = []; }
                // No completed 'output/' (failed/incomplete run) — recover the outputs
                // of the steps that DID finish from their work folders.
                if (!files.length) {
                    try {
                        const pr = await axios.get(`${API_BASE}/get-partial-results`, { params: { jobId: entry.job_id } });
                        partial = pr.data.files || [];
                    } catch (_) { /* none recoverable */ }
                }
            } else if (entry.workflow_type === "nfcore") {
                const res = await axios.get(`${API_BASE}/get-nfcore-results`, { params: { workflowId: entry.job_id } });
                files = (res.data.files || []).filter(f => !f.path.includes("pipeline_info/"));
            }
            const tree = buildTree(files);
            setResultsTree(prev => ({ ...prev, [jobIdStr]: tree }));
            setPartialResults(prev => ({ ...prev, [jobIdStr]: partial }));
            setSelectedFiles(prev => ({ ...prev, [jobIdStr]: {} }));
            setExpandedResults(prev => ({ ...prev, [jobIdStr]: true }));
        } catch (error) {
            console.error(`Error fetching results for job ${entry.job_id}:`, error);
            setResultsTree(prev => ({ ...prev, [jobIdStr]: {} }));
            setExpandedResults(prev => ({ ...prev, [jobIdStr]: true }));
        }
    };

    // Load interpretations list for an execution
    const loadInterpretations = async (executionId) => {
        const key = String(executionId);
        try {
            const res = await axios.get(`${API_BASE}/interpretation-list`, {
                params: { executionId: key },
                headers: { Authorization: localStorage.getItem("idToken") }
            });
            setInterpretationList(prev => ({
                ...prev,
                [key]: res.data.list || []
            }));
        } catch (err) {
            console.error("Failed to load interpretations:", err);
        }
    };

    const toggleInterpretationsPanel = async (entry) => {
        const jobIdStr = String(entry.job_id);
        const isExpanded = expandedInterpretations[jobIdStr];

        if (isExpanded) {
            setExpandedInterpretations(prev => ({ ...prev, [jobIdStr]: false }));
            return;
        }

        await loadInterpretations(jobIdStr);
        setExpandedInterpretations(prev => ({ ...prev, [jobIdStr]: true }));
        // If any job is still in flight, keep the list live until it settles.
        pollInterpretationList(jobIdStr);
    };

    // Toggle Report using HTML-from-backend + Blob URLs
    const toggleReportPanel = async (entry) => {
        const jobIdStr = String(entry.job_id);
        const isExpanded = expandedReports[jobIdStr];

        if (isExpanded) {
            setExpandedReports(prev => ({ ...prev, [jobIdStr]: false }));
            return;
        }

        if (reportHtmlBlobs[jobIdStr]) {
            setExpandedReports(prev => ({ ...prev, [jobIdStr]: true }));
            return;
        }

        try {
            const res = await axios.get(`${API_BASE}/get-execution-report`, {
                params: { jobId: entry.job_id }
            });

            const { html } = res.data || {};
            if (!html) {
                console.error("No HTML returned for jobId", entry.job_id);
                setExpandedReports(prev => ({ ...prev, [jobIdStr]: true }));
                return;
            }

            const blob = new Blob([html], { type: "text/html" });
            const url = URL.createObjectURL(blob);
            
            setReportHtmlBlobs(prev => ({ ...prev, [jobIdStr]: url }));
            setExpandedReports(prev => ({ ...prev, [jobIdStr]: true }));
        } catch (error) {
            console.error(`Error fetching execution report for job ${entry.job_id}:`, error);
            setExpandedReports(prev => ({ ...prev, [jobIdStr]: true }));
        }
    };

    // Open the pipeline's rich ANALYSIS report (output/report/index.html) in a new tab.
    // Served as a short-lived presigned URL (self-contained HTML — no server needed).
    const openAnalysisReport = async (entry) => {
        try {
            const res = await axios.get(`${API_BASE}/get-ots-report`, { params: { jobId: entry.job_id } });
            const url = res.data && res.data.url;
            if (url) window.open(url, "_blank", "noopener");
            else alert("No analysis report is available for this run.");
        } catch (error) {
            alert(error.response?.data?.error || "No analysis report is available for this run.");
        }
    };

    // Render tree: folders use synthetic path, files use full_s3_key as selection key
    const renderTree = (node, jobIdStr, path = "") => {
        return Object.values(node).map((item, idx) => {
            const fullPath =
                item.type === "folder"
                    ? `${path}/${item.name}`
                    : item.path; // full_s3_key

            const isChecked = selectedFiles[jobIdStr]?.[fullPath] || false;
            const isExpanded = expandedFolders[jobIdStr]?.[fullPath] || false;
            const fileName = item.displayPath ? item.displayPath.split("/").pop() : item.name;
            // Files can always be selected (the same checkboxes drive Download, which
            // supports every file type). Interpretation eligibility is shown as a hint
            // here and enforced at the Interpret button via a styled popup.
            const interpretable = item.type === "folder" || isAllowedForInterpretation(fileName);

            const toggleCheck = () => {
                if (item.type === "folder") return;
                setSelectedFiles(prev => ({
                    ...prev,
                    [jobIdStr]: {
                        ...prev[jobIdStr],
                        [fullPath]: !isChecked
                    }
                }));
            };

            const toggleExpand = () => {
                if (item.type !== "folder") return;
                setExpandedFolders(prev => ({
                    ...prev,
                    [jobIdStr]: {
                        ...prev[jobIdStr],
                        [fullPath]: !isExpanded
                    }
                }));
            };

            return (
                <div key={idx} style={{ marginLeft: "20px" }}>
                    {item.type === "folder" ? (
                        <div style={{ cursor: "pointer", fontWeight: "bold" }} onClick={toggleExpand}>
                            📁 {item.name}
                        </div>
                    ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                            <label
                                title={interpretable
                                    ? undefined
                                    : `Selectable for download, but not for interpretation. Interpretable types: ${interpretConfig.allowedFileTypes.join(", ")}`}
                            >
                                <input type="checkbox" checked={isChecked} onChange={toggleCheck} />
                                {fileName}
                                {typeof item.size === "number" && (
                                    <span style={{ fontSize: "11px", marginLeft: "6px", color: "#94a3b8" }}>
                                        ({formatBytes(item.size)})
                                    </span>
                                )}
                                {!interpretable && (
                                    <span style={{ fontSize: "11px", fontStyle: "italic", marginLeft: "6px", color: "#94a3b8" }}>
                                        (not interpretable)
                                    </span>
                                )}
                            </label>
                            {/\.html?$/i.test(fileName) && item.url && (
                                <button
                                    type="button"
                                    onClick={() => setHtmlViewModal({ url: item.url, title: fileName })}
                                    style={{
                                        fontSize: "11px", padding: "2px 8px", border: "1px solid #2f5d34",
                                        background: "#fff", color: "#2f5d34", borderRadius: "4px", cursor: "pointer", fontWeight: "bold",
                                    }}
                                >
                                    view
                                </button>
                            )}
                        </span>
                    )}
                    {item.type === "folder" && isExpanded && renderTree(item.children, jobIdStr, fullPath)}
                </div>
            );
        });
    };

    const formatBytes = (b) => {
        if (b == null || isNaN(b)) return "";
        if (b === 0) return "0 B";
        const u = ["B", "KB", "MB", "GB", "TB"];
        const i = Math.min(u.length - 1, Math.floor(Math.log(b) / Math.log(1024)));
        return `${(b / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
    };

    // Partial results from a FAILED/incomplete run: unique files from the steps that
    // finished, each a direct download link (presigned URL). Grouped by step.
    const renderPartialResults = (files) => {
        const byStep = {};
        files.forEach((f) => { (byStep[f.step] = byStep[f.step] || []).push(f); });
        return (
            <div className="partial-results">
                <div style={{ background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 6, padding: "8px 12px", marginBottom: 12, color: "#92400e", fontSize: 13 }}>
                    ⚠ <strong>Partial results</strong> — this workflow did not complete. Showing the outputs of the steps that finished successfully.
                </div>
                {Object.entries(byStep).map(([step, list]) => (
                    <div key={step} style={{ marginBottom: 10 }}>
                        <div style={{ fontWeight: "bold" }}>📁 {step}</div>
                        {list.map((f, i) => (
                            <div key={i} style={{ marginLeft: 20 }}>
                                <a href={f.url} target="_blank" rel="noopener noreferrer">{f.name}</a>
                                <span style={{ color: "#94a3b8", fontSize: 11, marginLeft: 8 }}>{formatBytes(f.size)}</span>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    const downloadSelectedFiles = async jobId => {
        const jobIdStr = String(jobId);
        const selections = selectedFiles[jobIdStr] || {};
        const selectedPaths = Object.entries(selections)
            .filter(([_, checked]) => checked)
            .map(([path]) => path); // full_s3_key

        if (!selectedPaths.length) return;
        if (downloadingJobs[jobIdStr]) return; // already compiling this run's zip

        setDownloadingJobs(prev => ({ ...prev, [jobIdStr]: true }));
        try {
            const response = await axios.post(`${API_BASE}/download-selected-files`, {
                workflowId: jobIdStr,
                selectedPaths,
            }, {
                responseType: "blob",
                headers: { Authorization: localStorage.getItem("idToken") }
            });

            const blob = new Blob([response.data], { type: "application/zip" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `results_${jobIdStr}.zip`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Download failed:", err);
            alert("Failed to download selected files.");
        } finally {
            setDownloadingJobs(prev => ({ ...prev, [jobIdStr]: false }));
        }
    };

    // Open the Save Files modal, prefilling the Project Name with the execution's S3
    // folder name (e.g. "2026_08_13T03_02_25_398Z-260").
    const openSaveModal = (entry) => {
        const folderName = String(entry.s3_folder_path || "").split("/").filter(Boolean).pop() || `execution_${entry.job_id}`;
        // Default: <WorkflowName with spaces/hyphens → underscores>_<execution folder name>
        const wfName = String(entry.short_name || "").trim().replace(/[\s-]+/g, "_");
        const defaultName = wfName ? `${wfName}_${folderName}` : folderName;
        setSaveModal({ jobId: entry.job_id, defaultName });
        setSaveProject(defaultName);
    };

    // While the Save Tasks view is open, poll every 4s so 'copying' → 'completed'/'failed'
    // updates live.
    useEffect(() => {
        if (!showSaveTasks) return;
        loadSaveTasks();
        const t = setInterval(loadSaveTasks, 4000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showSaveTasks]);

    // Human-friendly helpers for the save banner / tasks view.
    const fmtBytes = (b) => {
        const n = Number(b) || 0;
        if (n < 1024) return `${n} B`;
        const u = ["KB", "MB", "GB", "TB"]; let i = -1, v = n;
        do { v /= 1024; i++; } while (v >= 1024 && i < u.length - 1);
        return `${v.toFixed(1)} ${u[i]}`;
    };
    const fmtDuration = (s) => {
        const n = Math.max(1, Math.round(Number(s) || 0));
        if (n < 60) return `${n}s`;
        if (n < 3600) return `${Math.round(n / 60)} min`;
        return `${(n / 3600).toFixed(1)} hr`;
    };

    const loadSaveTasks = async () => {
        try {
            const res = await axios.get(`${API_BASE}/analysis/save-tasks`, { withCredentials: true });
            setSaveTasks(res.data.tasks || []);
        } catch (_) { /* ignore transient errors */ }
    };

    // Launch the save as a BACKGROUND copy: the request returns immediately with an
    // estimate, so we close the modal and show a non-blocking banner instead of hanging
    // the user while (potentially huge) files copy on the backend.
    const saveResults = async () => {
        if (!saveModal) return;
        const project = (saveProject || "").trim() || saveModal.defaultName;
        setSavingResults(true);
        try {
            const res = await axios.post(`${API_BASE}/analysis/save-results`, {
                jobId: saveModal.jobId,
                projectName: project,
            }, { withCredentials: true });
            const { totalFiles, totalBytes, estimateSeconds, project: proj } = res.data;
            setSaveModal(null);
            setSaveNotice(`Saving “${proj}” to Your Data in the background — ${totalFiles} file(s), ${fmtBytes(totalBytes)}. Estimated ~${fmtDuration(estimateSeconds)}. You can leave this page; track progress under “Save Tasks”.`);
            loadSaveTasks();
        } catch (err) {
            console.error("Save results failed:", err);
            alert(err.response?.data?.error || "Failed to save results.");
        } finally {
            setSavingResults(false);
        }
    };

    /* const launchJupyter = async (entry) => {
        const executionId = String(entry.job_id);
        console.log("Launching Jupyter for execution ID:", executionId);
        const resp = await fetch(`${API_BASE}/launch-notebook`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ executionId })
        });

        if (!resp.ok) {
            alert("Failed to launch notebook");
            return;
        }

        const data = await resp.json();
        console.log("launch-notebook response:", data);   
        // Go to the notebook status page
        navigate(`/notebook/${data.executionId}`);
    }; */

    /* const launchJupyter = async (entry) => {
        const executionId = String(entry.job_id);

        // Immediately navigate to the notebook page
        navigate(`/notebook/${executionId}`);

        // Fire the backend request in the background
        fetch(`${API_BASE}/launch-notebook`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ executionId })
        }).catch(err => {
            console.error("launch-notebook failed:", err);
        });
    }; */

    const getSelectedFilePaths = (jobId) => {
        const jobIdStr = String(jobId);
        const selections = selectedFiles[jobIdStr] || {};
        return Object.entries(selections)
            .filter(([_, checked]) => checked)
            .map(([path]) => path); // full_s3_key
    };

    // Refresh the Interpretations list for an execution every few seconds until
    // no job is still queued/running. This drives the dropdown's live status
    // (Pending -> Running -> View/Failed); it is decoupled from the Interpret
    // button, which only spins while a job is being SUBMITTED.
    const pollRefs = useRef({}); // executionId -> interval id

    async function pollInterpretationList(executionId) {
        const key = String(executionId);
        if (pollRefs.current[key]) return; // already polling this execution

        // Returns true while any job is still in flight.
        const poll = async () => {
            try {
                const res = await axios.get(`${API_BASE}/interpretation-list`, {
                    params: { executionId: key },
                    withCredentials: true
                });
                const list = res.data.list || [];
                setInterpretationList(prev => ({ ...prev, [key]: list }));
                return list.some(x => x.status === "queued" || x.status === "running");
            } catch (err) {
                console.error("Interpretation list poll error:", err);
                return true; // keep trying on transient errors
            }
        };

        const stillActive = await poll(); // immediate first refresh
        if (stillActive && !pollRefs.current[key]) {
            pollRefs.current[key] = setInterval(async () => {
                const active = await poll();
                if (!active) {
                    clearInterval(pollRefs.current[key]);
                    delete pollRefs.current[key];
                }
            }, 5000);
        }
    }

    // Clear any active pollers on unmount.
    useEffect(() => {
        return () => {
            Object.values(pollRefs.current).forEach(id => clearInterval(id));
            pollRefs.current = {};
        };
    }, []);

    // Submit a new interpretation. The button spins ONLY for the duration of this
    // POST; once the job is accepted we show it (Pending) in the dropdown and let
    // the list poller track it to completion.
    async function confirmInterpretation() {
        const executionId = interpretModalJobId;
        if (!executionId || executionId === "INSUFFICIENT_CREDITS") return;

        const filePaths = getSelectedFilePaths(executionId);
        if (filePaths.length === 0) {
            alert("Please select at least 1 file.");
            return;
        }
        if (filePaths.length > 10) {
            alert("You may select a maximum of 10 files.");
            return;
        }

        try {
            setIsInterpreting(prev => ({ ...prev, [executionId]: true }));
            // Auth is via the idToken cookie on the backend, so send credentials.
            await axios.post(
                `${API_BASE}/interpret-results`,
                {
                    workflowId,
                    executionId,
                    filePaths,
                    userInstructions: interpretationUserNotes || ""
                },
                { withCredentials: true }
            );

            // Close modal, reveal the Interpretations panel, and begin polling the
            // list so the new (Pending) job shows up immediately.
            setInterpretModalJobId(null);
            setInterpretationUserNotes("");
            setExpandedInterpretations(prev => ({ ...prev, [String(executionId)]: true }));
            await loadInterpretations(executionId);
            pollInterpretationList(executionId);
            alert("Interpretation launched. It will appear under the Interpretations panel for this run shortly.");
        } catch (err) {
            console.error("Failed to start interpretation:", err);
            alert(err.response?.data?.error || "Failed to start interpretation. Please try again.");
        } finally {
            setIsInterpreting(prev => ({ ...prev, [executionId]: false }));
        }
    }

    // Fetch a single interpretation report HTML and show in modal
    const viewInterpretationReport = async (interpretationId, executionId) => {
        try {
            const res = await axios.get(`${API_BASE}/interpretation-report`, {
                params: { id: interpretationId },
                headers: { Authorization: localStorage.getItem("idToken") }
            });
            const { report_html } = res.data || {};
            if (!report_html) {
                alert("No report HTML available for this interpretation.");
                return;
            }
            setInterpretationModalHtml(report_html);
            setInterpretationModalTitle(`Interpretation #${interpretationId} (Execution ${executionId})`);
        } catch (err) {
            console.error("Failed to fetch interpretation report:", err);
            alert("Failed to load interpretation report.");
        }
    };

    const [launching, setLaunching] = useState(false);
    const [launchStatus, setLaunchStatus] = useState("STARTING");
    const statusMessages = {
        STARTING: "Starting notebook environment…",
        PROVISIONING: "Provisioning compute resources…",
        AWAITING_INSTANCE: "Launching your notebook machine…",
        PREPARING_MOUNT: "Attaching your result files…",
        PENDING: "Preparing container…",
        RUNNING: "Notebook is starting…",
        READY: "Notebook is ready!",
        FAILED: "Notebook failed to start.",
        ERROR: "Unexpected error occurred."
    };

    // Notebook size config (fetched from backend) + launch popup state.
    const [notebookConfig, setNotebookConfig] = useState(null);
    const [notebookModalEntry, setNotebookModalEntry] = useState(null); // execution entry while the size popup is open
    const [selectedPreset, setSelectedPreset] = useState("small");

    useEffect(() => {
        const fetchNbConfig = async () => {
            try {
                const res = await axios.get(`${API_BASE}/notebook-config`, { withCredentials: true });
                setNotebookConfig(res.data);
            } catch (err) {
                console.error("Error fetching notebook config:", err);
            }
        };
        fetchNbConfig();
    }, []);

    // Open the size/pricing popup (called by the "Launch Jupyter" button).
    const openNotebookLauncher = (entry) => {
        setSelectedPreset("small");
        setNotebookModalEntry(entry);
    };

    const launchJupyter = (entry, preset) => {
        const executionId = String(entry.job_id);
        setNotebookModalEntry(null);
        setLaunching(true);
        setLaunchStatus("STARTING");

        const qp = preset ? `&preset=${encodeURIComponent(preset)}` : "";
        const evt = new EventSource(`${API_BASE}/launch-notebook?executionId=${executionId}${qp}`);

        evt.onmessage = (e) => {
            const status = e.data;
            setLaunchStatus(status);

            if (status === "READY") {
                evt.close();
                setLaunching(false);
                navigate(`/notebook/${executionId}`);
            }

            if (status === "FAILED") {
                evt.close();
                setLaunching(false);
                alert("Notebook failed to start.");
            }
        };
    };



    
    return (
    <>    
    <div className="analysis-container">
      <style>{`
        .cg-mini-spinner {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255,255,255,0.5);
          border-top-color: #fff;
          border-radius: 50%;
          animation: cg-mini-spin 0.7s linear infinite;
          vertical-align: middle;
        }
        @keyframes cg-mini-spin { to { transform: rotate(360deg); } }
      `}</style>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        <div>
          {executionHistory[0]?.short_name && (
            <h1 style={{ margin: 0, fontSize: 30, color: "#213a22" }}>{String(executionHistory[0].short_name).replace(/_/g, " ")}</h1>
          )}
          <div style={{ marginTop: 4, fontSize: 16, color: "#475569", fontWeight: 600 }}>Workflow Execution History</div>
        </div>
        <button
          onClick={() => { setShowSaveTasks(true); }}
          style={{ backgroundColor: "#2f5d34", color: "white", border: "none", padding: "8px 16px", borderRadius: 4, cursor: "pointer", fontWeight: "bold" }}
        >
          Save Tasks
        </button>
      </div>

      {saveNotice && (
        <div style={{ background: "#ecfdf5", border: "1px solid #6ee7b7", color: "#065f46", padding: "12px 16px", borderRadius: 8, marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <span>{saveNotice}</span>
          <span style={{ whiteSpace: "nowrap" }}>
            <button onClick={() => setShowSaveTasks(true)} style={{ background: "#059669", color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontWeight: "bold", marginRight: 8 }}>View Save Tasks</button>
            <button onClick={() => setSaveNotice(null)} style={{ background: "transparent", border: "none", color: "#065f46", cursor: "pointer", fontWeight: "bold" }}>Dismiss</button>
          </span>
        </div>
      )}

      {/* The former per-folder storage panel is merged into the execution table below
          (Total Size column + Remove icons in the Action column). */}

      <table className="styled-table analysis-table">
        <thead>
          <tr>
            <th>Run</th>
            <th>Status</th>
            <th>Start Time</th>
            <th>End Time</th>
            <th className="col-credits">Credits</th>
            <th>Total Size</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          {executionHistory.map((entry) => {
            const jobIdStr = String(entry.job_id);
            // Button spins ONLY while a job is being submitted, not while it runs.
            const submitting = !!isInterpreting[jobIdStr];
            // Per-folder storage actions (relaunches share a folder).
            const folderPath = entry.s3_folder_path;
            const runName = String(folderPath || "").split("/").filter(Boolean).pop() || entry.job_id;
            const removable = !!folderPath && folderPath.includes("/Analysis/");
            const active = folderActive(folderPath);
            const cleaning = !!cleaningPaths[folderPath];

            return (
              <React.Fragment key={jobIdStr}>
                <tr>
                  <td className="run-cell">
                    <div className="run-name" title={folderPath || runName}>{runName}</div>
                    <button
                      type="button"
                      className="run-details-toggle"
                      aria-expanded={!!expandedRunDetails[jobIdStr]}
                      onClick={() => setExpandedRunDetails((p) => ({ ...p, [jobIdStr]: !p[jobIdStr] }))}
                    >
                      <span className={`run-details-caret${expandedRunDetails[jobIdStr] ? " open" : ""}`}>▶</span>
                      Run Details
                    </button>
                    {expandedRunDetails[jobIdStr] && (
                      <div className="run-details">
                        <div><span className="rd-label">Execution ID:</span> {entry.id}</div>
                        <div><span className="rd-label">Launch Date &amp; Time:</span> {entry.created_on ? new Date(entry.created_on).toLocaleString() : "—"}</div>
                      </div>
                    )}
                  </td>
                  <td>
                    {(() => {
                      const st = String(entry.status || "").toLowerCase();
                      const isFail = st.startsWith("failed") || st === "user_error";
                      const isDone = st === "finished";
                      const icon = isDone ? "✅" : isFail ? "❌" : "⏳";
                      const label = entry.status.charAt(0).toUpperCase() + entry.status.slice(1);
                      return (
                        <span className="status-cell">
                          <span className={`status-icon ${isDone ? "ok" : isFail ? "fail" : "run"}`}>{icon}</span>
                          {label}
                        </span>
                      );
                    })()}
                  </td>
                  <td>{entry.created_on ? new Date(entry.created_on).toLocaleString() : "—"}</td>
                  <td>
                    {["finished", "failed", "user_error", "failed-terminal-failure", "failed-user_error"].includes(entry.status)
                      ? new Date(entry.status_time).toLocaleString()
                      : "—"}
                  </td>
                  <td className="col-credits">
                    {(() => {
                      // Charge is shown for EVERY terminal run — successful OR failed.
                      // BYOP (and resumed) runs are billed for compute whether they
                      // pass or fail, so failed/user_error runs still carry a charge.
                      const terminal = ["finished", "failed", "user_error", "failed-terminal-failure", "failed-user_error"].includes(entry.status);
                      if (!terminal) return "-";
                      if (entry.credits_charged != null) return Math.abs(entry.credits_charged).toFixed(2);
                      // Terminal but no ledger row yet: if the run reached AWS Batch
                      // (has a job_id) the billing service is still computing → spinner;
                      // if it never launched (no job_id) there is no compute → 0.00.
                      if (!entry.job_id) return (0).toFixed(2);
                      return (
                        <span className="charge-pending" title="Charge is being calculated">
                          <span className="charge-pending-spinner" />
                          Pending
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    {(() => {
                      const size = folderSizes[folderPath];
                      if (size === undefined || size === null) return <span style={{ color: "#94a3b8" }}>Calculating…</span>;
                      if (size < 0) return <span style={{ color: "#94a3b8" }}>—</span>;
                      return formatBytes(size);
                    })()}
                  </td>

                  <td>
                    {/* Failed/incomplete runs still expose a Results icon so users can
                        recover the outputs of the steps that DID complete (partial results). */}
                    {entry.status === "failed" && (
                      <button
                        className="results-toggle-button icon-action"
                        title={expandedResults[jobIdStr] ? "Hide Partial Results" : "Partial Results"}
                        aria-label="Partial Results"
                        onClick={() => toggleResultsPanel(entry)}
                      >
                        📁
                      </button>
                    )}
                    {entry.status === "finished" && (
                      <>
                        <button
                          className="results-toggle-button icon-action"
                          title={expandedResults[jobIdStr] ? "Hide Results" : "Results"}
                          aria-label="Results"
                          onClick={() => toggleResultsPanel(entry)}
                        >
                          📁
                        </button>

                        <button
                          className="report-toggle-button icon-action"
                          title={expandedReports[jobIdStr] ? "Hide Report" : "Report"}
                          aria-label="Report"
                          onClick={() => toggleReportPanel(entry)}
                        >
                          📄
                        </button>

                        <button
                          className="report-toggle-button icon-action"
                          title="Analysis Report"
                          aria-label="Analysis Report"
                          onClick={() => openAnalysisReport(entry)}
                        >
                          📊
                        </button>

                        <button
                          className="interpretation-button icon-action"
                          title={expandedInterpretations[jobIdStr] ? "Hide Interpretations" : "Interpretations"}
                          aria-label="Interpretations"
                          onClick={() => toggleInterpretationsPanel(entry)}
                        >
                          🧠
                        </button>

                        <button
                          className="launch-jupyter-button icon-action"
                          title="Launch Jupyter"
                          aria-label="Launch Jupyter"
                          disabled={launching}
                          onClick={() => openNotebookLauncher(entry)}
                        >
                          🪐
                        </button>
                      </>
                    )}

                    {["failed", "user_error", "failed-terminal-failure", "failed-user_error"].includes(entry.status) && (
                      <button
                        className="log-toggle-button icon-action"
                        title={expandedErrors[jobIdStr] ? "Hide Error" : "Show Error"}
                        aria-label="Show Error"
                        onClick={() => toggleErrorPanel(entry.job_id)}
                      >
                        ⚠️
                      </button>
                    )}

                    <button
                      className="log-toggle-button icon-action"
                      title={expandedPipeline[jobIdStr] ? "Hide Pipeline" : "Pipeline"}
                      aria-label="Pipeline"
                      onClick={() => togglePipeline(entry)}
                    >
                      ⚙️
                    </button>

                    <button
                      className="log-toggle-button icon-action"
                      aria-label="Remove run files"
                      disabled={!removable || active || cleaning}
                      title={
                        !removable ? "Only new-layout analyses apply"
                          : active ? "This run is still processing — wait until it finishes"
                          : "Remove intermediate run files (.nextflow + work) to free space; results/reports/logs are kept (run can't be resumed afterwards)"
                      }
                      onClick={() => removeRunFiles(folderPath)}
                    >
                      {cleaning ? <span className="cg-mini-spinner" style={{ borderColor: "rgba(0,0,0,0.25)", borderTopColor: "#334155" }} /> : "🧹"}
                    </button>

                    <button
                      className="log-toggle-button icon-action"
                      aria-label="Remove analysis"
                      disabled={!removable || active || cleaning}
                      title={
                        !removable ? "Only new-layout analyses can be removed here"
                          : active ? "This run is still processing — wait until it finishes"
                          : "Remove this analysis (delete all run files + remove it from history)"
                      }
                      onClick={() => setRemoveTarget({ path: folderPath, label: runName })}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>

                {expandedPipeline[jobIdStr] && (
                  <tr>
                    <td colSpan="7">
                      {(() => {
                        const pd = pipelineData[jobIdStr];
                        if (!pd) return <div style={{ padding: 12, color: "#64748b" }}>Loading pipeline…</div>;
                        if (pd.error) return <div style={{ padding: 12, color: "#dc2626" }}>{pd.error}</div>;
                        if (pd.status === "starting" || !(pd.processes && pd.processes.length))
                          return <div style={{ padding: 12, color: "#64748b" }}>Pipeline is starting — no trace yet (refreshes ~every 30s).</div>;
                        const s = pd.summary || {};
                        return (
                          <div style={{ margin: "6px 0", padding: "12px 14px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 10, fontSize: 12, color: "#475569" }}>
                              <span><strong>{s.done}</strong>/{s.total} done</span>
                              {s.running > 0 && <span style={{ color: "#2563eb" }}>{s.running} running</span>}
                              {s.failed > 0 && <span style={{ color: "#dc2626" }}>{s.failed} failed</span>}
                              {s.pending > 0 && <span>{s.pending} pending</span>}
                              <span style={{ marginLeft: "auto", color: "#94a3b8" }}>updated {new Date(pd.updatedAt).toLocaleTimeString()}</span>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                              {pd.processes.map((p, i) => {
                                const c = p.counts || {};
                                const detail = [
                                  c.COMPLETED ? `${c.COMPLETED} done` : null,
                                  c.CACHED ? `${c.CACHED} cached` : null,
                                  c.RUNNING ? `${c.RUNNING} running` : null,
                                  c.SUBMITTED ? `${c.SUBMITTED} queued` : null,
                                  c.FAILED ? `${c.FAILED} failed` : null,
                                ].filter(Boolean).join(" · ");
                                return (
                                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", background: "#fff", border: "1px solid #eef2f7", borderRadius: 6 }}>
                                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: statusColor(p.overallStatus), flex: "0 0 auto" }} />
                                    <span style={{ fontWeight: 600, minWidth: 220 }}>{p.name}</span>
                                    <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".5px", color: statusColor(p.overallStatus), fontWeight: 700 }}>{p.overallStatus}</span>
                                    <span style={{ marginLeft: "auto", fontSize: 12, color: "#64748b" }}>{p.taskCount > 0 ? (detail || `${p.taskCount} task(s)`) : "not started"}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                )}

                {["failed", "user_error", "failed-terminal-failure", "failed-user_error"].includes(entry.status) && expandedErrors[jobIdStr] && (
                  <tr>
                    <td colSpan="7">
                      <div
                        style={{
                          margin: "6px 0", padding: "12px 14px", borderRadius: 8,
                          border: "1px solid #fca5a5", background: "#fef2f2", color: "#7f1d1d",
                        }}
                      >
                        <div style={{ marginBottom: 6 }}>
                          <strong>❌ This run failed.</strong>
                          {entry.error && (
                            <span style={{ marginLeft: 8, fontSize: 12, background: "#fee2e2", padding: "1px 8px", borderRadius: 10 }}>
                              {String(entry.error).replace(/_/g, " ")}
                            </span>
                          )}
                        </div>
                        {entry.error_step && (
                          <div style={{ fontSize: 13, marginBottom: 6 }}>
                            <strong>Failed step(s):</strong> {entry.error_step}
                          </div>
                        )}
                        {entry.error_detail ? (
                          <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, background: "#1f2937", color: "#f3f4f6", padding: 12, borderRadius: 6, overflowX: "auto", margin: "6px 0 0" }}>
                            {entry.error_detail}
                          </pre>
                        ) : (
                          <div style={{ fontSize: 13 }}>No precise error was captured; view the full log below.</div>
                        )}
                        <button
                          className="log-toggle-button"
                          style={{ marginTop: 8 }}
                          onClick={() => fetchLogsForExecution(entry.job_id)}
                        >
                          View full log
                        </button>
                      </div>
                    </td>
                  </tr>
                )}

                                {expandedResults[jobIdStr] && (
                  <tr>
                    <td colSpan="7">
                      <div className="results-panel">
                        <div className="results-header">
                          <strong>Result Files</strong>

                          <button
                            className="download-selected-button"
                            onClick={() => downloadSelectedFiles(entry.job_id)}
                            disabled={!!downloadingJobs[String(entry.job_id)]}
                            style={{
                              float: "right",
                              backgroundColor: "#213a22",
                              color: "white",
                              border: "none",
                              padding: "8px 16px",
                              borderRadius: "4px",
                              cursor: downloadingJobs[String(entry.job_id)] ? "wait" : "pointer",
                              fontWeight: "bold",
                              opacity: downloadingJobs[String(entry.job_id)] ? 0.8 : 1,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "8px"
                            }}
                          >
                            {downloadingJobs[String(entry.job_id)]
                              ? <><span className="cg-mini-spinner" style={{ borderTopColor: "#fff", borderColor: "rgba(255,255,255,0.35)" }} /> Preparing…</>
                              : "Download"}
                          </button>

                          {entry.status === "finished" && (
                            <button
                              className="save-files-button"
                              onClick={() => openSaveModal(entry)}
                              style={{
                                float: "right",
                                marginRight: "8px",
                                backgroundColor: "#2f5d34",
                                color: "white",
                                border: "none",
                                padding: "8px 16px",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontWeight: "bold"
                              }}
                            >
                              Save Files
                            </button>
                          )}

                          <button
                            className="interpret-button"
                            disabled={submitting}
                            onClick={() => {
                              const selections = selectedFiles[jobIdStr] || {};
                              const selectedPaths = Object.entries(selections)
                                .filter(([_, checked]) => checked)
                                .map(([path]) => path);

                              if (selectedPaths.length === 0) {
                                alert("Please select at least 1 file.");
                                return;
                              }
                              if (selectedPaths.length > 10) {
                                alert("You may select a maximum of 10 files.");
                                return;
                              }

                              // Block disallowed file types with a styled popup.
                              const disallowed = selectedPaths.filter(
                                (p) => !isAllowedForInterpretation(p.split("/").pop())
                              );
                              if (disallowed.length) {
                                setDisallowedModal({ files: disallowed });
                                return;
                              }

                              if (insufficientCredits) {
                                setInterpretModalJobId("INSUFFICIENT_CREDITS");
                                return;
                              }

                              setInterpretModalJobId(jobIdStr);
                            }}
                            style={{
                              float: "right",
                              marginRight: "8px",
                              backgroundColor: "#213a22",
                              color: "white",
                              border: "none",
                              padding: "8px 16px",
                              borderRadius: "4px",
                              cursor: submitting ? "not-allowed" : "pointer",
                              fontWeight: "bold",
                              opacity: submitting ? 0.7 : 1
                            }}
                          >
                            {submitting
                              ? <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                                  <span className="cg-mini-spinner" /> Submitting…
                                </span>
                              : "Interpret"}
                          </button>
                        </div>

                        <div className="results-output">
                          {resultsTree[jobIdStr] && Object.keys(resultsTree[jobIdStr]).length
                            ? renderTree(resultsTree[jobIdStr], jobIdStr)
                            : (partialResults[jobIdStr]?.length
                                ? renderPartialResults(partialResults[jobIdStr])
                                : <div>No result files found.</div>)}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}

                {expandedReports[jobIdStr] && (
                  <tr>
                    <td colSpan="7">
                      <div className="report-panel">
                        <div className="report-header"><strong>Execution Report</strong></div>

                        {reportHtmlBlobs[jobIdStr] ? (
                          <iframe
                            src={reportHtmlBlobs[jobIdStr]}
                            title={`Execution Report ${jobIdStr}`}
                            style={{ width: "100%", height: "800px", border: "none" }}
                          />
                        ) : (
                          <div>No report found.</div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}

                {expandedInterpretations[jobIdStr] && (
                  <tr>
                    <td colSpan="7">
                      <div className="results-panel">
                        <div className="results-header">
                          <strong>Interpretations</strong>
                        </div>

                        <div className="results-output">
                          {(interpretationList[jobIdStr] || []).length === 0 ? (
                            <div>No interpretations found.</div>
                          ) : (
                            <table className="styled-table">
                              <thead>
                                <tr>
                                  <th>Files</th>
                                  <th>Credits Charge</th>
                                  <th>Created</th>
                                  <th>View</th>
                                </tr>
                              </thead>

                              <tbody>
                                {(interpretationList[jobIdStr] || []).map(item => (
                                  <tr key={item.id}>
                                    <td>
                                      {(item.file_list || []).map((f, i) => (
                                        <div key={i}>{String(f).split("/").pop()}</div>
                                      ))}
                                    </td>

                                    <td>
                                      {item.charge != null
                                        ? `${Math.abs(item.charge).toFixed(2)}`
                                        : "-"}
                                    </td>

                                    <td>{new Date(item.created_at).toLocaleString()}</td>

                                    <td>
                                      {item.status === "complete" ? (
                                        <button
                                          onClick={() =>
                                            viewInterpretationReport(item.id, jobIdStr)
                                          }
                                        >
                                          View
                                        </button>
                                      ) : item.status === "failed" ? (
                                        <span style={{ color: "#dc2626", fontWeight: "bold" }}>
                                          Failed
                                        </span>
                                      ) : item.status === "running" ? (
                                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: "#64748b" }}>
                                          <span
                                            className="cg-mini-spinner"
                                            style={{ borderColor: "rgba(100,116,139,0.4)", borderTopColor: "#64748b" }}
                                          />
                                          Running…
                                        </span>
                                      ) : (
                                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: "#64748b" }}>
                                          <span
                                            className="cg-mini-spinner"
                                            style={{ borderColor: "rgba(100,116,139,0.4)", borderTopColor: "#64748b" }}
                                          />
                                          Pending…
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {selectedLog && (
        <div className="logs-container">
          <h3>Logs for Selected Execution</h3>
          <pre>{selectedLog}</pre>
        </div>
      )}
          {interpretModalJobId && (
        <div className="interpret-modal-backdrop">
          <div className="interpret-modal">
            <h3>
              {interpretModalJobId === "INSUFFICIENT_CREDITS"
                ? "Insufficient Credits"
                : "Confirm Interpretation"}
            </h3>

            <p>
              {interpretModalJobId === "INSUFFICIENT_CREDITS"
                ? "You have less than 50 Credits available."
                : "You are about to start an interpretation. This is a charged action."}
            </p>

            {interpretModalJobId !== "INSUFFICIENT_CREDITS" && (
              <>
                <label style={{ display: "block", marginTop: "12px", fontWeight: "bold" }}>
                  Add notes for interpretation (optional)
                </label>

                <textarea
                  value={interpretationUserNotes}
                  onChange={(e) => setInterpretationUserNotes(e.target.value)}
                  style={{
                    width: "100%",
                    minHeight: "80px",
                    marginTop: "4px",
                    padding: "6px",
                    borderRadius: "4px",
                    border: "1px solid #ccc"
                  }}
                />
              </>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button onClick={() => setInterpretModalJobId(null)}>Close</button>

              <button
                disabled={interpretModalJobId === "INSUFFICIENT_CREDITS"}
                onClick={confirmInterpretation}
                style={{
                  backgroundColor:
                    interpretModalJobId === "INSUFFICIENT_CREDITS" ? "#999" : "#213a22",
                  color: "white",
                  padding: "6px 12px",
                  borderRadius: "4px",
                  cursor:
                    interpretModalJobId === "INSUFFICIENT_CREDITS"
                      ? "not-allowed"
                      : "pointer"
                }}
              >
                Confirm & Interpret
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REMOVE ANALYSIS CONFIRMATION MODAL */}
      {htmlViewModal && (
        <div
          onClick={() => setHtmlViewModal(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", width: "92%", height: "92%", borderRadius: 10, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 50px rgba(0,0,0,0.35)" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #e5e7eb", gap: 12 }}>
              <strong style={{ fontSize: 14, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{htmlViewModal.title}</strong>
              <span style={{ whiteSpace: "nowrap", display: "inline-flex", gap: 12, alignItems: "center" }}>
                <a href={htmlViewModal.url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#2563eb" }}>Open in new tab ↗</a>
                <button onClick={() => setHtmlViewModal(null)} style={{ background: "#2f5d34", color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontWeight: "bold" }}>Close</button>
              </span>
            </div>
            <iframe src={htmlViewModal.url} title={htmlViewModal.title} style={{ flex: 1, width: "100%", border: "none" }} />
          </div>
        </div>
      )}

      {removeTarget && (
        <div
          className="interpret-modal-backdrop"
          onClick={() => !removing && setRemoveTarget(null)}
        >
          <div className="interpret-modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: "#213a22" }}>Remove analysis?</h3>
            <p style={{ color: "#334155" }}>
              This permanently deletes the analysis folder{" "}
              <strong style={{ wordBreak: "break-all" }}>{removeTarget.label}</strong>{" "}
              from S3 — including its results and all execution records for this run.
              This cannot be undone.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "16px" }}>
              <button onClick={() => setRemoveTarget(null)} disabled={removing}>
                Cancel
              </button>
              <button
                onClick={confirmRemoveAnalysis}
                disabled={removing}
                style={{
                  backgroundColor: "#dc2626",
                  color: "white",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  cursor: removing ? "not-allowed" : "pointer",
                  fontWeight: "bold",
                  opacity: removing ? 0.7 : 1,
                }}
              >
                {removing ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                    <span className="cg-mini-spinner" /> Removing…
                  </span>
                ) : (
                  "Remove"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SAVE RESULTS MODAL — prompt for a Project Name (defaults to the run's folder) */}
      {saveModal && (
        <div
          className="interpret-modal-backdrop"
          onClick={() => !savingResults && setSaveModal(null)}
        >
          <div className="interpret-modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: "#213a22" }}>Save results to Your Data</h3>
            <p style={{ color: "#334155" }}>
              All result files for this run will be copied to your data library under the
              project name below, where you can reuse them as pipeline inputs.
            </p>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#475569", margin: "8px 0 4px" }}>Project name</label>
            <input
              value={saveProject}
              onChange={(e) => setSaveProject(e.target.value)}
              placeholder={saveModal.defaultName}
              style={{ width: "100%", padding: "8px", border: "1px solid #cbd5e1", borderRadius: 6, boxSizing: "border-box" }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "16px" }}>
              <button onClick={() => setSaveModal(null)} disabled={savingResults}>Cancel</button>
              <button
                onClick={saveResults}
                disabled={savingResults}
                style={{
                  backgroundColor: "#213a22", color: "white", border: "none",
                  padding: "8px 16px", borderRadius: "4px",
                  cursor: savingResults ? "not-allowed" : "pointer", fontWeight: "bold",
                  opacity: savingResults ? 0.7 : 1,
                }}
              >
                {savingResults
                  ? <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}><span className="cg-mini-spinner" /> Saving…</span>
                  : "Save Files"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SAVE TASKS STATUS VIEW */}
      {showSaveTasks && (
        <div className="interpret-modal-backdrop" onClick={() => setShowSaveTasks(false)}>
          <div className="interpret-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820, width: "92%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ marginTop: 0, color: "#213a22" }}>Save Tasks</h3>
              <button onClick={() => setShowSaveTasks(false)}>Close</button>
            </div>
            <p style={{ color: "#64748b", marginTop: 0, fontSize: 13 }}>
              Background copies of run results into Your Data. This list refreshes automatically.
            </p>
            <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead><tr>
                  {["Project", "Status", "Files", "Size", "Started"].map((x) => (
                    <th key={x} style={{ textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #e2e8f0", color: "#475569" }}>{x}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {saveTasks.map((t) => {
                    const color = t.status === "completed" ? "#16a34a" : t.status === "failed" ? "#dc2626" : "#d97706";
                    return (
                      <tr key={t.id}>
                        <td style={{ padding: "8px 10px", borderBottom: "1px solid #eef2f7", fontWeight: 600 }}>{t.project_name}</td>
                        <td style={{ padding: "8px 10px", borderBottom: "1px solid #eef2f7", color, fontWeight: 600, textTransform: "capitalize" }}>
                          {t.status === "copying"
                            ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><span className="cg-mini-spinner" style={{ borderTopColor: color, borderColor: "rgba(0,0,0,0.15)" }} /> Copying…</span>
                            : t.status}
                          {t.status === "failed" && t.error ? <div style={{ fontSize: 11, color: "#991b1b", fontWeight: 400 }}>{t.error}</div> : null}
                        </td>
                        <td style={{ padding: "8px 10px", borderBottom: "1px solid #eef2f7" }}>{t.status === "completed" ? t.saved_files : t.total_files}</td>
                        <td style={{ padding: "8px 10px", borderBottom: "1px solid #eef2f7" }}>{fmtBytes(t.total_bytes)}</td>
                        <td style={{ padding: "8px 10px", borderBottom: "1px solid #eef2f7", color: "#64748b" }}>{new Date(t.created_at).toLocaleString()}</td>
                      </tr>
                    );
                  })}
                  {!saveTasks.length && <tr><td colSpan={5} style={{ padding: "12px 10px", color: "#64748b" }}>No save tasks yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* DISALLOWED FILE TYPES MODAL (styled, not window.alert) */}
      {disallowedModal && (
        <div
          onClick={() => setDisallowedModal(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 8, width: "440px", maxWidth: "92%",
              padding: "22px 24px", boxShadow: "0 10px 30px rgba(15,23,42,0.25)"
            }}
          >
            <h3 style={{ margin: "0 0 8px", color: "#213a22" }}>
              Some files can't be interpreted
            </h3>
            <p style={{ margin: "0 0 10px", color: "#334155" }}>
              The following selected file{disallowedModal.files.length > 1 ? "s are" : " is"} not a
              supported type for AI interpretation. Please unselect{" "}
              {disallowedModal.files.length > 1 ? "them" : "it"} and try again.
            </p>
            <ul style={{ margin: "0 0 12px", paddingLeft: "18px", color: "#dc2626", maxHeight: 160, overflowY: "auto" }}>
              {disallowedModal.files.map((f, i) => (
                <li key={i} style={{ wordBreak: "break-all" }}>{f.split("/").pop()}</li>
              ))}
            </ul>
            <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#64748b" }}>
              Interpretable types: {interpretConfig.allowedFileTypes.join(", ")}
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => setDisallowedModal(null)}
                style={{
                  backgroundColor: "#213a22", color: "white", border: "none",
                  padding: "8px 16px", borderRadius: "4px", cursor: "pointer", fontWeight: "bold"
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* NOTEBOOK SIZE / PRICING POPUP (shown before launch) */}
    {notebookModalEntry && (
      <div
        onClick={() => setNotebookModalEntry(null)}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#fff", borderRadius: 8, width: "480px", maxWidth: "92%",
            padding: "22px 24px", boxShadow: "0 10px 30px rgba(15,23,42,0.25)"
          }}
        >
          <h3 style={{ margin: "0 0 6px", color: "#213a22" }}>Launch Jupyter Notebook</h3>
          <p style={{ margin: "0 0 14px", color: "#334155" }}>
            Choose the compute size for this session.
          </p>

          <label style={{ display: "block", fontWeight: "bold", marginBottom: 6 }}>
            Notebook size
          </label>
          <select
            value={selectedPreset}
            onChange={(e) => setSelectedPreset(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: 4, border: "1px solid #ccc", marginBottom: 16 }}
          >
            {(notebookConfig?.presets || [{ key: "small", label: "Small — 4 vCPU / 16 GB" }]).map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>

          <div style={{
            background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8,
            padding: "10px 12px", fontSize: 13, color: "#92400e", marginBottom: 8
          }}>
            ⚠️ This is a <strong>paid session</strong>. You are billed for the compute
            you select for as long as the notebook runs.
          </div>
          <div style={{
            background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8,
            padding: "10px 12px", fontSize: 13, color: "#1e40af", marginBottom: 18
          }}>
            💤 An idle notebook is automatically shut down after{" "}
            <strong>{notebookConfig?.idleTimeoutMinutes ?? 10} minutes</strong> of inactivity
            (billing stops when it shuts down).
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={() => setNotebookModalEntry(null)}>Cancel</button>
            <button
              onClick={() => launchJupyter(notebookModalEntry, selectedPreset)}
              style={{
                backgroundColor: "#213a22", color: "white", border: "none",
                padding: "8px 16px", borderRadius: 4, cursor: "pointer", fontWeight: "bold"
              }}
            >
              Launch
            </button>
          </div>
        </div>
      </div>
    )}

    {/* FLOATING LAUNCH NOTEBOOK MODAL */}
    {launching && (
      <div className="modal-backdrop">
        <div className="modal">
          <div className="spinner"></div>
          <h2>Launching notebook…</h2>
          <p>{statusMessages[launchStatus] || launchStatus}</p>
        </div>
      </div>
    )}

    {/* INTERPRETATION REPORT MODAL */}
    {interpretationModalHtml !== null && (
      <div
        onClick={() => setInterpretationModalHtml(null)}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#fff", borderRadius: 8, width: "90%", height: "90%",
            display: "flex", flexDirection: "column", overflow: "hidden"
          }}
        >
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "10px 14px", borderBottom: "1px solid #e2e8f0"
          }}>
            <strong>{interpretationModalTitle || "Interpretation report"}</strong>
            <button onClick={() => setInterpretationModalHtml(null)}>Close</button>
          </div>
          <iframe
            title="interpretation-report"
            srcDoc={interpretationModalHtml}
            style={{ flex: 1, width: "100%", border: "none" }}
          />
        </div>
      </div>
    )}
  </>
);

};

export default AnalysisView; 